/**
 * The renderer's words for what it learned in #76, #96, #26 and #1: a bulk action's preview and
 * its full report, an answer that is a file, a removal named in its confirmation, the declared text
 * rules and ranges, the named formats, and the export's totals and data start. Kept beside the
 * catalogue and spread into it, so a parallel change to the catalogue does not collide with these.
 */
export const RENDERER_EN = {
    'list.bulk.preview.notApplies': 'The server says the action does not apply to this row',
    'list.bulk.preview.description': 'Each row as the server says the action would leave it, before anything runs. The server judges each row again when it runs.',
    'list.bulk.preview.asking': 'Asking the server what each row would come to…',
    'list.bulk.preview.unavailable': 'This action asks the server before it runs, and there is nowhere to ask here.',
    'list.bulk.preview.skipWhy': 'rows the action is not offered on, or the server says it does not apply to',
    'list.bulk.preview.row': 'Row',
    'list.bulk.preview.verdict': 'Will it run',
    'list.bulk.preview.applies': 'Will run',
    // A form's preview (`Action.preview`): what sending it would come to, drawn before it is sent.
    'form.preview.title': 'What this comes to',
    'form.preview.waiting': 'Fill in the required fields to see what this comes to.',
    'form.preview.asking': 'Asking the server what this would come to…',
    'form.preview.unavailable': 'Preview unavailable. The form can still be sent; the server checks it then.',
    'form.preview.notApplies': 'The server says this would not go through as it stands.',
    'list.bulk.report.title.refused': '{done} done — {refused} refused',
    'list.bulk.report.title.done': '{done} done',
    'list.bulk.report.description': 'Each refused row with the reason the server gave. The rest went through, and the figures above the list were read again.',
    'list.bulk.report.reason': 'Why',
    'list.bulk.report.allDone': 'The action went through on every row.',
    'action.download.notAFile': 'The answer was not a file, so there is nothing to save.',
    'action.destructive.about': 'This applies to: {name}',
    'action.about': 'For: {name}',
    'field.format.iban': 'SA followed by 22 digits, without spaces.',
    'field.format.iqama': '10 digits, starting with 2.',
    'field.pattern': 'This is not written the way this field asks.',
    'field.length.min': 'At least {min} characters.',
    'field.length.max': 'At most {max} characters.',
    'field.range.min': 'At least {min}.',
    'field.range.max': 'At most {max}.',
    'field.range.between': 'Between {min} and {max}.',
    'field.wholeNumber': 'A whole number, without a fraction.',
    'export.totals': 'Total of the rows in this file',
    'export.meta.dataFrom': 'Data held from',
    'list.filter.notApplicable': 'not applicable',
    'list.filter.notApplicable.why': 'This report has no such dimension, so nothing is narrowed by it.',
    // The `conversation` display: a transcript a row carries — a report's conversation with the assistant.
    'cell.conversation.user': 'Person',
    'cell.conversation.assistant': 'Assistant',
    // A phone column's `channels: ["whatsapp"]`: the small link beside the number, and what it says aloud.
    'cell.whatsapp': 'WhatsApp',
    // Said after a highlighted figure, for a reader who cannot see its pill; the pause is the language's own comma.
    'cell.highlight': ', needs action',
    'cell.whatsapp.open': 'Open a WhatsApp chat with {number}',
    'unit.messages.zero': '{n} messages',
    'unit.messages.one': '{n} message',
    'unit.messages.two': '{n} messages',
    'unit.messages.few': '{n} messages',
    'unit.messages.many': '{n} messages',
    'unit.messages.other': '{n} messages',
};
export const RENDERER_AR = {
    'list.bulk.preview.notApplies': 'يقول الخادم إن الإجراء لا ينطبق على هذا الصفّ',
    'list.bulk.preview.description': 'كل صفّ كما يقول الخادم إن الإجراء سيتركه، قبل أن يُنفَّذ شيء. ويحكم الخادم على كل صفّ مرّة أخرى عند التنفيذ.',
    'list.bulk.preview.asking': 'يُسأل الخادم عمّا سيؤول إليه كل صفّ…',
    'list.bulk.preview.unavailable': 'هذا الإجراء يسأل الخادم قبل تنفيذه، ولا سبيل إلى سؤاله هنا.',
    'list.bulk.preview.skipWhy': 'صفوف لا يُعرض عليها الإجراء أو يقول الخادم إنه لا ينطبق عليها',
    'list.bulk.preview.row': 'الصفّ',
    'list.bulk.preview.verdict': 'هل يُنفَّذ',
    'list.bulk.preview.applies': 'سيُنفَّذ',
    'form.preview.title': 'ما سيؤول إليه',
    'form.preview.waiting': 'املأ الحقول المطلوبة لترى ما سيؤول إليه.',
    'form.preview.asking': 'يُسأل الخادم عمّا سيؤول إليه…',
    'form.preview.unavailable': 'المعاينة غير متاحة. يمكن إرسال النموذج، ويتحقّق منه الخادم عندئذٍ.',
    'form.preview.notApplies': 'يقول الخادم إن هذا لن يمرّ على حاله.',
    'list.bulk.report.title.refused': 'تمّ {done} — رُفض {refused}',
    'list.bulk.report.title.done': 'تمّ {done}',
    'list.bulk.report.description': 'كل صفّ رُفض مع السبب الذي ذكره الخادم. ومرّ الباقي، وأُعيدت قراءة الأرقام فوق القائمة.',
    'list.bulk.report.reason': 'السبب',
    'list.bulk.report.allDone': 'نُفّذ الإجراء على كل الصفوف.',
    'action.download.notAFile': 'لم يكن الجواب ملفاً، فلا شيء يُحفظ.',
    'action.destructive.about': 'ينطبق هذا على: {name}',
    'action.about': 'بشأن: {name}',
    'field.format.iban': 'SA متبوعة بـ 22 رقماً، بلا مسافات.',
    'field.format.iqama': '10 أرقام، تبدأ بـ 2.',
    'field.pattern': 'لم يُكتب هذا بالصيغة التي يطلبها الحقل.',
    'field.length.min': 'لا يقلّ عن {min} حرفاً.',
    'field.length.max': 'لا يزيد على {max} حرفاً.',
    'field.range.min': 'لا يقلّ عن {min}.',
    'field.range.max': 'لا يزيد على {max}.',
    'field.range.between': 'بين {min} و{max}.',
    'field.wholeNumber': 'عدد صحيح، بلا كسور.',
    'export.totals': 'مجموع صفوف هذا الملف',
    'export.meta.dataFrom': 'البيانات متاحة منذ',
    'list.filter.notApplicable': 'لا ينطبق',
    'list.filter.notApplicable.why': 'ليس لهذا التقرير هذا البُعد، فلا يُضيَّق به شيء.',
    'cell.conversation.user': 'الشخص',
    'cell.conversation.assistant': 'المساعد',
    'cell.whatsapp': 'واتساب',
    'cell.highlight': '، يحتاج إجراء',
    'cell.whatsapp.open': 'فتح محادثة واتساب مع {number}',
    'unit.messages.zero': 'لا رسائل',
    'unit.messages.one': 'رسالة واحدة',
    'unit.messages.two': 'رسالتان',
    'unit.messages.few': '{n} رسائل',
    'unit.messages.many': '{n} رسالة',
    'unit.messages.other': '{n} رسالة',
};
//# sourceMappingURL=i18n-renderer.js.map